import { AddOrEditMatterTaskPlanTaskModal } from "@/components/features/settings/defaults/matter-task-plans/AddOrEditMatterTaskPlanTaskModal";
import { MatterTaskPlanTaskContextMenu } from "@/components/features/settings/defaults/matter-task-plans/MatterTaskPlanTaskContextMenu";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  ExpandableText,
  Text,
} from "@/components/ui";
import { useDeleteMatterTaskPlanTask } from "@/lib/data/react-query/matters/matter-task-plan";
import { useMatterTaskPlanActions } from "@/hooks/use-matter-task-plan-actions";
import type { MatterSettingStaticOption } from "@/lib/data/requests/matters/matter-setting-statics";
import { getDirectoryUsersByIdsOptions } from "@/lib/data/react-query/firm/firm-user";
import type {
  MatterRole,
  MatterStatus,
  MatterTaskPlanTask,
  DirectoryUser,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatUsersName } from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, Pencil, Plus, PlusCircle } from "lucide-react";
import pluralize from "pluralize";
import { useMemo, useState } from "react";

type TaskWithRoles = MatterTaskPlanTask & {
  assigneeRole?: MatterRole;
  assigneeUsers: Array<DirectoryUser | undefined>;
  assigneeBackupUser?: DirectoryUser;
  assignerRole?: MatterRole;
  assignerUsers: Array<DirectoryUser | undefined>;
  assignerBackupUser?: DirectoryUser;
  parentTask: MatterTaskPlanTask | undefined;
};

function getAssignmentUserIds(
  userIds: string[] | undefined,
  userId: string | null | undefined,
): string[] {
  return userIds?.length ? userIds : userId ? [userId] : [];
}

function getTaskTriggerSummary(
  task: TaskWithRoles,
  statuses: MatterStatus[],
  actionOptions: MatterSettingStaticOption[],
): string {
  if (task.triggerStatusId) {
    const status = statuses.find((item) => item.id === task.triggerStatusId);
    return status ? `Matter Status - ${status.label}` : "Matter Status";
  }
  if (task.triggerSubitem) {
    const subitem = actionOptions.find(
      (option) => option.value === task.triggerSubitem,
    );
    return subitem ? `Action Taken - ${subitem.label}` : "Action Taken";
  }
  if (task.triggerTaskId) {
    const parentTitle = task.parentTask?.title ?? "task";
    return `Task Completion - ${parentTitle}`;
  }
  return "—";
}

function formatDayOffset(raw: MatterTaskPlanTask["dueDate"]): string | null {
  if (raw === undefined || raw === null) {
    return null;
  }
  const n = typeof raw === "number" ? raw : Number(raw);
  if (Number.isNaN(n)) {
    return null;
  }
  return pluralize("day", n, true);
}

function formatAssignment(
  role: MatterRole | undefined,
  roleId: string | null | undefined,
  users: Array<DirectoryUser | undefined>,
  userIds: string[],
  areUsersLoading: boolean,
): string {
  if (role) {
    return role.label;
  }
  if (roleId) {
    return "Unknown role";
  }
  if (userIds.length > 0) {
    return users
      .map((user) =>
        user
          ? (formatUsersName(user) ?? "Unknown user")
          : areUsersLoading
            ? "Loading..."
            : "Unknown user",
      )
      .join(", ");
  }
  return "Unassigned";
}

function formatBackupUser(
  user: DirectoryUser | undefined,
  userId: string | null | undefined,
  areUsersLoading: boolean,
): string | null {
  if (!userId) {
    return null;
  }
  if (!user) {
    return areUsersLoading ? "Loading..." : "Unknown user";
  }
  return formatUsersName(user) ?? "Unknown user";
}

const PRIORITY_BADGE: Partial<
  Record<NonNullable<MatterTaskPlanTask["priority"]>, string>
> = {
  Low: "border-transparent bg-slate-100 text-slate-700",
  Medium: "border-transparent bg-blue-100 text-blue-800",
  High: "border-transparent bg-amber-100 text-amber-900",
  Urgent: "border-transparent bg-red-100 text-red-800",
};

function TaskPlanTaskCard({
  task,
  statuses,
  actionOptions,
  childrenByParentId,
  onDelete,
  onEdit,
  canDelete,
  canUpdate,
  areUsersLoading,
}: {
  task: TaskWithRoles;
  statuses: MatterStatus[];
  actionOptions: MatterSettingStaticOption[];
  childrenByParentId: Map<string, TaskWithRoles[]>;
  onDelete: (task: TaskWithRoles) => void;
  onEdit: (task: TaskWithRoles) => void;
  canDelete: boolean;
  canUpdate: boolean;
  areUsersLoading: boolean;
}) {
  const startLine = formatDayOffset(task.startDate);
  const dueLine = formatDayOffset(task.dueDate);
  const assigneeBackupUser = formatBackupUser(
    task.assigneeBackupUser,
    task.assigneeBackupUserId,
    areUsersLoading,
  );
  const assignerBackupUser = formatBackupUser(
    task.assignerBackupUser,
    task.assignerBackupUserId,
    areUsersLoading,
  );
  const priorityClass =
    task.priority && PRIORITY_BADGE[task.priority]
      ? PRIORITY_BADGE[task.priority]
      : "border-border bg-muted/60 text-foreground";
  const childTasks = childrenByParentId.get(task.id) ?? [];
  const assigneeUserIds = getAssignmentUserIds(
    task.assigneeUserIds,
    task.assigneeUserId,
  );
  const assignerUserIds = getAssignmentUserIds(
    task.assignerUserIds,
    task.assignerUserId,
  );

  return (
    <>
      <MatterTaskPlanTaskContextMenu
        task={task}
        canUpdate={canUpdate}
        canDelete={canDelete}
        onEdit={() => {
          onEdit(task);
        }}
        onDelete={() => {
          onDelete(task);
        }}
        trigger={
          <div className="border-border bg-card hover:bg-light-primary/50 group flex cursor-pointer gap-3 rounded-lg border p-4 shadow-xs transition-colors">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 gap-y-1">
                <Text variant="body" className="text-sm">
                  {task.title}
                </Text>
                {task.priority ? (
                  <Badge
                    className={cn(
                      "text-xs font-medium capitalize",
                      priorityClass,
                    )}
                  >
                    {task.priority.toLowerCase()}
                  </Badge>
                ) : null}
              </div>
              {task.description ? (
                <ExpandableText
                  text={task.description}
                  maxLines={2}
                  className="text-muted-foreground mt-2 font-normal whitespace-pre-wrap"
                />
              ) : null}
              <Text variant="caption" className="mt-2 leading-relaxed">
                <span className="font-medium">Trigger:</span>{" "}
                <span>
                  {getTaskTriggerSummary(task, statuses, actionOptions)}
                </span>
              </Text>
              {startLine ? (
                <Text variant="caption" className="leading-relaxed">
                  <span className="font-medium">Start:</span>{" "}
                  <span>{startLine} after trigger</span>
                </Text>
              ) : null}
              {dueLine ? (
                <Text variant="caption" className="leading-relaxed">
                  <span className="font-medium">Due:</span>{" "}
                  <span>{dueLine} after trigger</span>
                </Text>
              ) : null}
              <Text variant="caption" className="leading-relaxed">
                <span className="font-medium">Repeatable:</span>{" "}
                <span>{task.repeatable ? "Yes" : "No"}</span>
              </Text>
              <Text variant="caption" className="leading-relaxed">
                <span className="font-medium">
                  {task.assignerRoleId
                    ? "Assigner Role:"
                    : assignerUserIds.length > 1
                      ? "Assigners:"
                      : "Assigner:"}
                </span>{" "}
                <span>
                  {formatAssignment(
                    task.assignerRole,
                    task.assignerRoleId,
                    task.assignerUsers,
                    assignerUserIds,
                    areUsersLoading,
                  )}
                </span>
              </Text>
              {assignerBackupUser ? (
                <Text variant="caption" className="leading-relaxed">
                  <span className="font-medium">Assigner backup user:</span>{" "}
                  <span>{assignerBackupUser}</span>
                </Text>
              ) : null}
              <Text variant="caption" className="leading-relaxed">
                <span className="font-medium">
                  {task.assigneeRoleId
                    ? "Assignee Role:"
                    : assigneeUserIds.length > 1
                      ? "Assignees:"
                      : "Assignee:"}
                </span>{" "}
                <span>
                  {formatAssignment(
                    task.assigneeRole,
                    task.assigneeRoleId,
                    task.assigneeUsers,
                    assigneeUserIds,
                    areUsersLoading,
                  )}
                </span>
              </Text>
              {assigneeBackupUser ? (
                <Text variant="caption" className="leading-relaxed">
                  <span className="font-medium">Assignee backup user:</span>{" "}
                  <span>{assigneeBackupUser}</span>
                </Text>
              ) : null}
            </div>
            {canUpdate ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 w-9 shrink-0 p-0"
                onClick={() => {
                  onEdit(task);
                }}
              >
                <span className="sr-only">Edit {task.title}</span>
                <Pencil className="text-muted-foreground h-4 w-4" aria-hidden />
              </Button>
            ) : null}
          </div>
        }
      />
      {childTasks.length > 0 ? (
        <div className="flex flex-col items-center gap-3 py-1">
          <div className="text-primary flex flex-col items-center">
            <Text variant="caption" className="text-primary font-medium">
              On completion
            </Text>
            <ArrowDown className="h-5 w-5" aria-hidden />
          </div>
          <div className="w-full space-y-3">
            {childTasks.map((childTask) => (
              <TaskPlanTaskCard
                key={childTask.id}
                task={childTask}
                statuses={statuses}
                actionOptions={actionOptions}
                childrenByParentId={childrenByParentId}
                onDelete={onDelete}
                onEdit={onEdit}
                canDelete={canDelete}
                canUpdate={canUpdate}
                areUsersLoading={areUsersLoading}
              />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * Task list for Matters → Settings → Task Plans: card rows (screenshot-style),
 * not a table. Edit opens {@link AddOrEditMatterTaskPlanTaskModal} (modal).
 */
export function TaskPlanTasksPanel({
  id,
  tasks,
  statuses,
  roles,
  canCreate,
  canDelete,
  canUpdate,
}: {
  id: string;
  tasks: MatterTaskPlanTask[];
  statuses: MatterStatus[];
  roles: MatterRole[];
  canCreate: boolean;
  canDelete: boolean;
  canUpdate: boolean;
}) {
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [editingTask, setEditingTask] = useState(false);
  const [activeTask, setActiveTask] = useState<TaskWithRoles>();
  const userIds = useMemo(
    () =>
      [
        ...new Set(
          tasks
            .flatMap((task) => [
              ...getAssignmentUserIds(
                task.assigneeUserIds,
                task.assigneeUserId,
              ),
              task.assigneeBackupUserId,
              ...getAssignmentUserIds(
                task.assignerUserIds,
                task.assignerUserId,
              ),
              task.assignerBackupUserId,
            ])
            .filter((id): id is string => Boolean(id)),
        ),
      ].sort(),
    [tasks],
  );
  const { data: usersData, isLoading: areUsersLoading } = useQuery(
    getDirectoryUsersByIdsOptions(userIds),
  );
  const {
    actionOptions,
    isLoading: areActionOptionsLoading,
    isError: isActionOptionsError,
  } = useMatterTaskPlanActions();
  const users = useMemo(() => usersData ?? [], [usersData]);
  const [taskPendingDelete, setTaskPendingDelete] = useState<TaskWithRoles>();
  const deleteTask = useDeleteMatterTaskPlanTask();

  const tasksWithRoles = useMemo((): TaskWithRoles[] => {
    return tasks.map((task) => {
      const assigneeUserIds = getAssignmentUserIds(
        task.assigneeUserIds,
        task.assigneeUserId,
      );
      const assignerUserIds = getAssignmentUserIds(
        task.assignerUserIds,
        task.assignerUserId,
      );
      return {
        ...task,
        assigneeRole: roles.find((role) => role.id === task.assigneeRoleId),
        assigneeUsers: assigneeUserIds.map((userId) =>
          users.find((user) => user.id === userId),
        ),
        assigneeBackupUser: task.assigneeBackupUserId
          ? users.find((user) => user.id === task.assigneeBackupUserId)
          : undefined,
        assignerRole: roles.find((role) => role.id === task.assignerRoleId),
        assignerUsers: assignerUserIds.map((userId) =>
          users.find((user) => user.id === userId),
        ),
        assignerBackupUser: task.assignerBackupUserId
          ? users.find((user) => user.id === task.assignerBackupUserId)
          : undefined,
        parentTask: tasks.find(
          (parentTask) => parentTask.id === task.triggerTaskId,
        ),
      };
    });
  }, [roles, tasks, users]);
  const { childrenByParentId, taskGroups } = useMemo(() => {
    const tasksById = new Set(tasksWithRoles.map((task) => task.id));
    const childrenByParentId = new Map<string, TaskWithRoles[]>();
    const rootTasks: TaskWithRoles[] = [];

    for (const task of tasksWithRoles) {
      if (task.triggerTaskId && tasksById.has(task.triggerTaskId)) {
        const children = childrenByParentId.get(task.triggerTaskId) ?? [];
        children.push(task);
        childrenByParentId.set(task.triggerTaskId, children);
      } else {
        rootTasks.push(task);
      }
    }

    const taskGroups = new Map<string, TaskWithRoles[]>();
    for (const task of rootTasks) {
      const trigger = getTaskTriggerSummary(task, statuses, actionOptions);
      const group = taskGroups.get(trigger) ?? [];
      group.push(task);
      taskGroups.set(trigger, group);
    }

    return {
      childrenByParentId,
      taskGroups: Array.from(taskGroups, ([trigger, tasks]) => ({
        trigger,
        tasks,
      })),
    };
  }, [actionOptions, statuses, tasksWithRoles]);

  const isReadOnly = !canUpdate;

  const openEdit = (task: TaskWithRoles) => {
    setEditingTask(true);
    setActiveTask(task);
    setIsAddingTask(false);
  };

  const openCreate = () => {
    setEditingTask(false);
    setActiveTask(undefined);
    setIsAddingTask(true);
  };

  const canCreateTask = canCreate && !isReadOnly;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Text
          variant="caption"
          className="font-semibold tracking-wide uppercase"
        >
          Tasks
        </Text>
        {canCreateTask && tasksWithRoles.length > 0 ? (
          <Button
            variant="outline"
            type="button"
            size="sm"
            onClick={openCreate}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Add task
          </Button>
        ) : null}
      </div>

      {tasksWithRoles.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <Text variant="subHeading">
            No tasks have been created for this plan.
          </Text>
          {canCreateTask ? (
            <Button type="button" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add task
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-6">
          {taskGroups.map((group) => (
            <section key={group.trigger} className="space-y-3">
              <Text variant="caption" className="font-semibold">
                {group.trigger}
              </Text>
              <div className="space-y-3">
                {group.tasks.map((task) => (
                  <TaskPlanTaskCard
                    key={task.id}
                    task={task}
                    statuses={statuses}
                    actionOptions={actionOptions}
                    childrenByParentId={childrenByParentId}
                    onDelete={setTaskPendingDelete}
                    onEdit={openEdit}
                    canDelete={canDelete}
                    canUpdate={canUpdate}
                    areUsersLoading={areUsersLoading}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {isAddingTask ? (
        <AddOrEditMatterTaskPlanTaskModal
          open={isAddingTask}
          setOpen={setIsAddingTask}
          id={id}
          tasks={tasks}
          roles={roles}
          statuses={statuses}
          actionOptions={actionOptions}
          areActionOptionsLoading={areActionOptionsLoading}
          isActionOptionsError={isActionOptionsError}
          editing={false}
        />
      ) : null}

      {editingTask && activeTask ? (
        <AddOrEditMatterTaskPlanTaskModal
          open={editingTask}
          setOpen={(open) => {
            setEditingTask(open);
            if (!open) {
              setActiveTask(undefined);
            }
          }}
          id={id}
          tasks={tasks}
          roles={roles}
          statuses={statuses}
          actionOptions={actionOptions}
          areActionOptionsLoading={areActionOptionsLoading}
          isActionOptionsError={isActionOptionsError}
          editing
          activeTask={activeTask}
          isReadOnly={isReadOnly}
        />
      ) : null}
      <ActionConfirmationModal
        title="Are you sure you want to delete this task?"
        open={Boolean(taskPendingDelete)}
        setOpen={(open) => {
          if (!open) setTaskPendingDelete(undefined);
        }}
        isActionPending={deleteTask.isPending}
        afterActionFn={async () => {
          if (!taskPendingDelete) return;
          await deleteTask.mutateAsync({
            id: taskPendingDelete.id,
            taskPlanId: id,
          });
          setTaskPendingDelete(undefined);
        }}
      />
    </div>
  );
}
